import * as React from 'react';
import { cn } from '@/lib/utils/cn';

/**
 * Form fields for workspace screens (T-0185): the prototype's `.input`,
 * `.field-label` and `.field-help` (`docs/requirement-library/prototype/
 * tokens.css`). Several screens had copied the same class string; new
 * screens use these.
 *
 *   <WorkspaceField label="Population" help="Where it came from" error={problem}>
 *     {(id) => <WorkspaceInput id={id} … />}
 *   </WorkspaceField>
 */

export const workspaceInputClass =
  'block h-input-h w-full rounded-card border border-ink-200 bg-paper-0 px-3 text-body text-ink-900 placeholder:text-ink-300 hover:border-ink-300 focus:outline focus:outline-2 focus:outline-offset-2 focus:outline-accent-amber-600 disabled:cursor-not-allowed disabled:bg-paper-50 disabled:text-ink-500';

export const WorkspaceInput = React.forwardRef<
  HTMLInputElement,
  React.InputHTMLAttributes<HTMLInputElement>
>(({ className, ...props }, ref) => (
  <input ref={ref} className={cn(workspaceInputClass, className)} {...props} />
));
WorkspaceInput.displayName = 'WorkspaceInput';

export const WorkspaceSelect = React.forwardRef<
  HTMLSelectElement,
  React.SelectHTMLAttributes<HTMLSelectElement>
>(({ className, ...props }, ref) => (
  <select ref={ref} className={cn(workspaceInputClass, 'pr-8', className)} {...props} />
));
WorkspaceSelect.displayName = 'WorkspaceSelect';

export interface WorkspaceFieldProps {
  label: React.ReactNode;
  /** Line under the control (`.field-help`). */
  help?: React.ReactNode;
  /** A problem with the value; replaces the help line and marks the control invalid. */
  error?: string | null;
  className?: string;
  children: (id: string, describedBy: string | undefined) => React.ReactNode;
}

export function WorkspaceField({ label, help, error, className, children }: WorkspaceFieldProps) {
  const id = React.useId();
  const noteId = `${id}-note`;
  const note = error ?? help;
  return (
    <div className={cn('min-w-0', className)}>
      <label htmlFor={id} className="mb-1.5 block text-meta font-medium text-ink-700">
        {label}
      </label>
      {children(id, note ? noteId : undefined)}
      {note ? (
        <div
          id={noteId}
          role={error ? 'alert' : undefined}
          className={cn(
            'mt-1.5 flex items-center gap-1 text-meta',
            error ? 'text-posture-critical' : 'text-ink-500',
          )}
        >
          {note}
        </div>
      ) : null}
    </div>
  );
}
